import { useState, useEffect, useMemo } from "react";
import { Layers, Search } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import ChipsOrigen, { claveOrigen, etiquetaOrigen } from "./ChipsOrigen";   // Fix EC

const opts = { withCredentials: true };

// Fix DA (2026-09-15): "esta ventana me gustaría que se vea igual a la de presupuesto,
// con los encabezados y las columnas de costo proyectado… y agregar un buscador tipo
// googlesheets… sirve para comparar con la distribución de fondos en la ventana de
// tareas" (Edson, 15/09). Son las mismas columnas de la Estructura de Cómputo, en el
// mismo orden, y las secciones (5.0 ALIMENTACIONES PRINCIPALES) que acá se estaban
// filtrando.
const COLUMNAS = [
  ["C.U. MAT", "costo_unit_mat"],
  ["Tot.Mat S/IVA", "mat_sin_iva"],
  ["Total Mat.", "total_mat"],
  ["C.U. M.O", "costo_unit_mo"],
  ["Tot.MO S/IVA", "mo_sin_iva"],
  ["Total M.O.", "total_mo"],
  ["Costo Dir.", "costo_total"],
];

const num = (v) => Number(v || 0).toLocaleString("es-PY");

export default function PresupuestoTab({ obra, canFullAccess }) {
  const [rows, setRows] = useState(null);
  const [totales, setTotales] = useState(null);
  const [sinCalcular, setSinCalcular] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // Fix EC (2026-09-24): "PRESUPUESTO; lo mismo, sólo se visualiza aparentemente el
  // contrato inicial y adicionales no". Los adicionales están al final de la lista;
  // ahora se filtran y cada fila dice de dónde viene.
  const [origen, setOrigen] = useState("todos");
  const adicionalesIds = obra.adicionales_ids || [];

  useEffect(() => {
    setLoading(true);
    if (canFullAccess) {
      axios.get(`${API}/obras/${obra._id || obra.id}/presupuesto-detalle`, opts)
        .then(({ data }) => {
          setRows(data.items || []);
          setTotales(data.totales || null);
          setSinCalcular(data.sin_calcular || 0);
        })
        .catch(() => {
          // Si el detalle no está disponible, se cae a lo que ya había: la obra en
          // memoria, sin el desglose de costo. Nunca queda la pantalla en blanco.
          setRows((obra.items || []).map(it => ({ ...it, calculado: false })));
          setTotales(null);
        })
        .finally(() => setLoading(false));
    } else {
      // Fix EC (2026-09-24): el base y cada adicional, marcados con su origen.
      const ids = [obra.presupuesto_base_id, ...(obra.adicionales_ids || [])].filter(Boolean);
      Promise.all(ids.map(bid => axios.get(`${API}/budgets/${bid}/items-para-pedido`, opts)
        .then(({ data }) => (data.items || []).map(it => ({
          ...it,
          origen_tipo: bid === obra.presupuesto_base_id ? "base" : "adicional",
          origen_budget_id: bid,
        })))))
        .then(listas => setRows(listas.flat()))
        .catch(() => toast.error("Error cargando el presupuesto"))
        .finally(() => setLoading(false));
    }
  }, [obra, canFullAccess]);

  // Buscador tipo planilla: filtra por lo que se ve (N°, descripción, unidad) sin
  // distinguir mayúsculas ni acentos, y conserva la SECCIÓN de cada fila que queda, para
  // no perder de vista a qué capítulo pertenece lo que estás mirando.
  const visibles = useMemo(() => {
    const todas = (rows || []).filter(r => origen === "todos" || claveOrigen(r) === origen);   // Fix EC
    const t = q.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (!t) return todas;
    const limpio = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    const coincide = (r) => `${r.numero} ${r.descripcion} ${r.unidad}`;
    const out = [];
    let seccionPendiente = null;
    for (const r of todas) {
      if (r.is_group) { seccionPendiente = r; continue; }
      if (limpio(coincide(r)).includes(t)) {
        if (seccionPendiente) { out.push(seccionPendiente); seccionPendiente = null; }
        out.push(r);
      }
    }
    return out;
  }, [rows, q, origen]);

  // Fix EC (2026-09-24): con un presupuesto elegido, el total es el de ese presupuesto.
  const totalesOrigen = useMemo(() => {
    if (origen === "todos") return null;
    const t = {};
    COLUMNAS.forEach(([, k]) => {
      t[k] = (rows || []).filter(r => !r.is_group && claveOrigen(r) === origen)
        .reduce((s, r) => s + (Number(r[k]) || 0), 0);
    });
    return t;
  }, [rows, origen]);
  const totalesMostrados = totalesOrigen || totales;

  if (loading) return <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>;

  const cols = canFullAccess ? COLUMNAS : [];
  const anchoTotal = 4 + cols.length;

  return (
    <div className="space-y-3" data-testid="obra-presupuesto-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-gray-400 flex items-center gap-1.5"><Layers size={13} /> {canFullAccess ? "Vista completa (coordinación)" : "Vista sin montos — solo cantidades y unidades"}</p>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar ítem, N° o unidad..."
            className="border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-xs w-64 focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/20"
            data-testid="obra-presupuesto-buscar" />
        </div>
      </div>
      <ChipsOrigen adicionalesIds={adicionalesIds} valor={origen} onChange={setOrigen} testId="obra-presupuesto-origen" />
      {q.trim() && (
        <p className="text-[11px] text-gray-400" data-testid="obra-presupuesto-resultados">
          {visibles.filter(r => !r.is_group).length} de {(rows || []).filter(r => !r.is_group).length} ítems
        </p>
      )}
      {sinCalcular > 0 && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="obra-presupuesto-sin-calcular">
          {sinCalcular} ítem(s) sin costo proyectado: no se encontraron en su presupuesto de origen. Se muestran con guion en vez de un número que no cuadraría.
        </p>
      )}
      <div className="overflow-x-auto rounded-2xl border border-gray-200/60 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#2E6DA4] text-white text-left uppercase text-[11px]">
              <th className="px-3 py-2.5 font-semibold">N°</th>
              {/* Fix EU (2026-10-01): en el celular la descripción no se aprieta */}
              <th className="px-3 py-2.5 font-semibold max-lg:min-w-[220px]">Descripción</th>
              <th className="px-3 py-2.5 font-semibold">Un.</th>
              <th className="px-3 py-2.5 text-right font-semibold">Cant.</th>
              {cols.map(([h]) => <th key={h} className="px-3 py-2.5 text-right font-semibold whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {visibles.map((it, i) => it.is_group ? (
              // Las secciones del presupuesto, que antes se filtraban y por eso la
              // pantalla iba de 4.10 a 5.1 sin decir que empezaba otro capítulo.
              <tr key={it.item_uid || `g-${i}`} className="bg-[#EBF3FB]" data-testid={`presupuesto-seccion-${i}`}>
                <td className="px-3 py-2 font-mono text-xs font-bold text-[#1E3A5F]">{it.numero}</td>
                <td className="px-3 py-2 font-bold text-[#1E3A5F] uppercase" colSpan={3 + cols.length}>{it.descripcion}</td>
              </tr>
            ) : (
              <tr key={it.item_uid || i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/40"} data-testid={`presupuesto-row-${i}`}>
                <td className="px-3 py-2 text-gray-500 font-mono text-xs">{it.numero}</td>
                <td className="px-3 py-2 text-gray-800">
                  {it.descripcion}
                  {claveOrigen(it) !== "base" && (
                    <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`presupuesto-origen-${i}`}>
                      {etiquetaOrigen(it, adicionalesIds)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-500 text-xs">{it.unidad}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(it.cantidad)}</td>
                {cols.map(([h, k]) => (
                  <td key={k} className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${k === "costo_total" ? "font-semibold text-gray-800" : "text-gray-600"}`}
                      data-testid={`presupuesto-${k}-${i}`}>
                    {it[k] == null ? <span className="text-gray-300">—</span> : formatPYG(it[k])}
                  </td>
                ))}
              </tr>
            ))}
            {visibles.length === 0 && (
              <tr><td colSpan={anchoTotal} className="px-3 py-8 text-center text-gray-400 text-xs">Sin coincidencias</td></tr>
            )}
          </tbody>
          {totales && totalesMostrados && !q.trim() && (
            <tfoot>
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold text-xs" data-testid="obra-presupuesto-totales">
                <td colSpan={4} className="px-3 py-2.5 text-gray-600 uppercase">
                  {origen === "todos" ? "Total de la obra" : `Total ${origen === "base" ? "Base" : etiquetaOrigen({ origen_tipo: "adicional", origen_budget_id: origen }, adicionalesIds)}`}
                </td>
                {cols.map(([h, k]) => (
                  <td key={k} className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-[#2E6DA4]" data-testid={`presupuesto-total-${k}`}>
                    {formatPYG(totalesMostrados[k] || 0)}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
